/**
 * A Drop's stored data -> the props the spine's sections take.
 *
 * Named for the SPINE, not for the anchors. An anchor is a design spec we keep
 * locally as a reference; it is not a thing that exists in this codebase, so a
 * module or a folder named after one tells a reader to go looking for
 * something that was never here. The capability is the spine — the structure
 * every POV wears — and that is what these files are.
 *
 * `dropSlots.ts` answers WHERE a surface's parts go. This answers WHAT is in
 * them. Both are joins between what an operator authored and what the spine
 * understands, and both must stay the only places that speak two vocabularies
 * — a section component that learned about `DropSectionType` would have to
 * learn about every future host type too.
 *
 * ## Absent is absent
 *
 * Every field here is optional, and nothing is defaulted into existence. The
 * anchor shows a full bar because Modern Warfare 4 has a lockup, a launch
 * window, a synopsis and live counters; a surface with none of that gets the
 * parts it has and no placeholders. A "0 contributing" invented to fill the
 * shape puts a number on the page that means nothing, and the whole reason
 * those counters earn their place is that they mean something.
 *
 * This is also what makes the optionality claim real rather than asserted:
 * RTTR drops 36 of the spine's slots, and it can only do that if every one of
 * them renders nothing when its data is missing.
 */

import React from "react";
import type {SurfaceTopBarProps} from "./SurfaceTopBar";
import type {SurfaceRailNavItem} from "./SurfaceRailNav";
import type {SurfaceStageProps, SurfaceStageStat} from "./SurfaceStage";
import {sectionIcon} from "./sectionIcons";
import type {SurfaceRosterMember} from "./SurfaceRosterRail";
import type {SurfaceNewsItem} from "./SurfaceNewsRail";
import type {SurfaceStripCard} from "./SurfaceStrip";
import {povCopy} from "./povCopy";
/*
 * NO LOCAL ROSTER TYPES — see the note beside `isOffTitle` below for why
 * `RosterMember`/`memberKey` come from `@/widgets/rosterMember` and not a
 * type declared in this file.
 */
import {type RosterMember, memberKey} from "@/widgets/rosterMember";
import type {SurfaceThemeColors} from "@/hooks/hub/useGetHub";
import {resolveRosterCard, RosterLiveState, isOffTitleFor as isOffTitle} from "@/widgets/rosterCard";
import {type RosterFieldSpec} from "@devour/client";

/*
 * ── DATES ARE FORMATTED HERE, IN THE VIEWER'S LOCALE ──
 *
 * `release.releaseDate` is an epoch. Printing it raw puts "1792713600000" in
 * the phase line, and hard-coding "Oct 23" puts an American month order in
 * front of everyone. `Intl` with no explicit locale uses the viewer's own,
 * which is the only correct answer for a date a publisher announces globally.
 */
const MONTH_DAY = new Intl.DateTimeFormat(undefined, {month: "short", day: "numeric"});

/**
 * What the top bar calls each release phase.
 *
 * Copy, kept beside the mapping that uses it rather than inline at the call
 * site, so all three read together and a fourth status added to the enum is a
 * visible hole rather than an undefined that renders nothing.
 */
const RELEASE_PHASE_LABEL: Record<string, string> = {
    ANNOUNCED: "Launch window",
    BETA: "Beta",
    RELEASED: "Out now",
};

/** How the date reads under each phase — ahead of the title, or behind it. */
const RELEASE_DATE_PREFIX: Record<string, string> = {
    ANNOUNCED: "Launches",
    BETA: "Launches",
    RELEASED: "Released",
};

/** Whole days from now until `when`, or undefined when it is not in the future. */
function daysUntil(when: number, now: number): number | undefined {
    const ms = when - now;
    if (ms <= 0) return undefined;
    return Math.ceil(ms / 86_400_000);
}

/** The subset of a Drop this needs. Kept minimal so it survives schema growth. */
export interface SpineDrop {
    name: string;
    /** How many creators are on the attached roster. Counted, never guessed. */
    rosterCount?: number;
    /** Opted-in membership count, computed by the slug route. */
    memberCount?: number;
    /** Authored copy for the member-count stat's label. */
    memberStatLabel?: string;
    /** Authored qualifier under the member count — "Worldwide", "Joined". */
    memberStatDetail?: string;
    /** The operator's chosen point of view — drives `povCopy()`. */
    pov?: string;
    /** Legacy fallback signal, same one `dropLayout` reads when `pov` is unset. */
    dropType?: string;
    slug?: string;
    /** The operator's authored palette — the top bar reads `primary` for its accent. */
    themeColors?: SurfaceThemeColors;
    tagline?: string;
    synopsis?: string;
    description?: string;
    logoImageUrl?: string;
    purchaseUrl?: string;
    trailerUrl?: string;
    heroSection?: { heroImageUrl?: string; heroImageExternalUrl?: string; heroImageRotationUrls?: string[] };
    /*
     * The stored shape is `{releaseDate, status}` — an epoch and an enum, not a
     * `date`/`phase` pair. Written from the API response rather than from the
     * field names the anchor's copy uses, because a type that describes what we
     * wish were stored compiles perfectly and reads `undefined` at runtime.
     */
    release?: { releaseDate?: number; status?: string };
    /*
     * The campaign's own windows — a named span with a start and an end.
     *
     * This is what a BRAND campaign has instead of a release. Road to the
     * Finals does not launch; it runs, in rounds, and its stage says "Road to
     * the Finals is live" with time left in the current round. A surface built
     * only around `release` had nothing true to say about it.
     */
    campaignWindows?: ReadonlyArray<{ name?: string; startsAt?: number; endsAt?: number }>;
    /** Authored program wordmark — the bar's program slot. Display copy only. */
    programName?: string;
    sections?: ReadonlyArray<{
        _id?: string;
        id?: string;
        type?: string;
        label?: string;
        title?: string;
        /** Authored second line. Live state wins over it — see `railItems`. */
        sublabel?: string;
        icon?: string;
        enabled?: boolean;
        /*
         * What the section points at — the sublabel hook resolves live
         * state from the referenced engagement.
         */
        engagementType?: string;
        engagementId?: string;
    }>;
}


/*
 * ONE PICK PER PAGE LOAD, held here.
 *
 * This is a pure builder called during render, and the memo above it keys on
 * the drop OBJECT — which React Query replaces on every refetch. So re-rolling
 * inside the function meant the hero visibly changed under the reader whenever
 * a query settled, an auth state flipped or a socket invalidated: "why does it
 * start with one picture and then jump to another?" (Shelly, 2026-09-06).
 *
 * Module scope IS the page load. It resets on navigation-with-reload and on a
 * fresh tab, which is exactly the cadence the rotation wants, and it makes the
 * choice independent of how many times anything re-renders. Keyed by the pool
 * itself, so re-authoring the rotation picks again rather than serving a stale
 * url that is no longer in the set.
 */
const heroPickCache = new Map<string, string>();

/**
 * The hero's background for THIS page load.
 *
 * A campaign that shot several key arts had one field to put one of them in, so
 * a surface looked identical every visit for the length of a run. The authored
 * rotation is picked from once per load — not cross-faded on a timer, which
 * animates behind live copy and costs every image up front for a change nobody
 * asked to watch.
 *
 * `heroImageUrl` leads and is part of the set, so a surface that never authors
 * a rotation resolves exactly as it did before this existed.
 */
export function pickHeroImage(drop: SpineDrop): string | undefined {
    const primary = drop.heroSection?.heroImageUrl ?? drop.heroSection?.heroImageExternalUrl;
    const rotation = drop.heroSection?.heroImageRotationUrls ?? [];
    /* Deduped: the primary is usually also in the authored list, and counting
       it twice would make it twice as likely as its siblings. */
    const pool = [...new Set([...primary ? [primary] : [], ...rotation])];
    if (pool.length <= 1) return pool[0] ?? primary;

    const key = pool.join("|");
    const held = heroPickCache.get(key);
    if (held) return held;

    const picked = pool[Math.floor(Math.random() * pool.length)];
    heroPickCache.set(key, picked);
    return picked;
}

/** English ordinal ("31st"). Presentation, so it lives with the join. */
export function ordinal(n: number): string {
    const rem100 = n % 100;
    if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
    const suffix = ({1: "st", 2: "nd", 3: "rd"} as Record<number, string>)[n % 10] ?? "th";
    return `${n}${suffix}`;
}

/**
 * The board's clock, as the standing line's period word. A type with no row
 * contributes nothing — a wrong period word reads as authored copy.
 */
export const BOARD_PERIOD_WORD: Partial<Record<string, string>> = {
    WEEKLY: "this week",
    DAILY: "today",
    MONTHLY: "this month",
};

/** "Week N" from a numeric periodKey. Non-numeric keys yield nothing. */
export function weekLabel(periodKey: string): string | undefined {
    const n = Number(periodKey);
    return Number.isFinite(n) && n >= 0 ? `Week ${n + 1}` : undefined;
}

/**
 * The rail's items, read off the surface's sections.
 *
 * The rail is not one OF the sections — it is a view of them, which is why
 * `dropSlots` emits it carrying no section id. The label is the operator's
 * (`label`, falling back to `title`); nothing is title-cased or reworded here,
 * because renaming an operator's section in the nav means the nav and the
 * section it points at disagree.
 *
 * ## Sublabels — only the ONE we can derive honestly
 *
 * `SurfaceRailNav`'s sublabel slot has sat empty since it was built: nothing
 * called `railItems` with anything to put there. The anchor's own examples
 * ("Beta → Oct 23", "38 live now") are a mix of two different things — a
 * release window and a live count — and only the second has a field behind
 * it here. `release` is a Drop-level date/status pair, not a per-SECTION one,
 * so there is no section this rail could honestly hang "Beta → Oct 23" off
 * of without guessing which item the anchor meant it for. The roster's live
 * count is real, per-STREAMING-section data, so that is the one sublabel this
 * derives — everything else stays a single line, which is correct per
 * `SurfaceRailNav`'s own contract: absent is absent, not a placeholder.
 */
export function railItems(
    drop: SpineDrop,
    opts?: {
        streamingLiveCount?: number;
        /**
         * Live second lines keyed by SECTION id, resolved by the caller
         * (useGetSectionSublabels) from the sections' engagements. State
         * beats authored copy: a resolved line wins over the derived
         * streaming count and over any static sublabel, because "6 days"
         * is truer than whatever was typed at authoring time.
         */
        sublabels?: Record<string, string>;
    },
): SurfaceRailNavItem[] {
    return (drop.sections ?? [])
        .filter(s => s.enabled !== false)
        .map((s, i) => ({
            id: s._id ?? s.id ?? `section-${i}`,
            label: s.label ?? s.title ?? s.type ?? "",
            /*
             * The stored icon name, resolved to a glyph. Sections carry this
             * field and nothing in web read it, so every rail item rendered
             * with an empty tile beside it.
             */
            icon: sectionIcon(s.icon, s.type, s.engagementType),
            /*
             * Undefined while the roster hasn't loaded yet — "0 live now" on
             * a first paint would be true of nothing and false as soon as the
             * fetch resolves, which is worse than the line being briefly
             * absent.
             */
            sublabel: opts?.sublabels?.[s._id ?? s.id ?? `section-${i}`] ??
                (s.type === "STREAMING" && opts?.streamingLiveCount !== undefined
                    ? `${opts.streamingLiveCount} live now`
                    /*
                     * The authored line is the FLOOR, not the answer: it shows
                     * on the sections that have no state to derive from, and
                     * yields the moment there is some.
                     */
                    : s.sublabel || undefined),
        }))
        .filter(item => item.label !== "");
}

/**
 * The top bar.
 *
 * `phaseLabel`/`phaseDetail` come from `release`, which is the launch layer —
 * a date and a phase. It is deliberately NOT a layout signal (a publisher
 * surface is a publisher surface with or without one), but it IS what the bar's
 * window block says, and a surface with no release simply has no window block.
 */
export function topBarProps(
    drop: SpineDrop,
    opts?: {
        /** Pre-formatted week line for the campaign form. Supplied ONLY when a board exists. */
        campaignWeekLabel?: string;
    },
): SurfaceTopBarProps {
    const date = drop.release?.releaseDate;
    const status = drop.release?.status;
    return {
        logoUrl: drop.logoImageUrl,
        name: drop.name,
        /*
         * AUTHORED, never derived: the program wordmark is its own field on
         * the surface. When it is set the bar takes the campaign form —
         * [lockup][program][this surface's own name] — and the release-phase
         * window yields the subtitle slot to the surface's name. When it is
         * absent nothing changes: the publisher form below stands.
         */
        wordmark: drop.programName,
        /*
         * DERIVED FROM `release.status`, not a constant.
         *
         * This said "Launch window" for every surface — the anchor's copy for
         * Modern Warfare 4, typed in as though it were a platform label. A Drop
         * that has shipped would still have announced a launch window, and one
         * in beta would have said nothing about the beta.
         *
         * `release.status` is a real enum (ANNOUNCED | BETA | RELEASED) and is
         * exactly the question this line answers: what phase is this surface
         * in. The words are HERE rather than inline so there is one place to
         * change them and one place to see all three, which is the only way a
         * fourth status gets noticed instead of silently rendering blank.
         *
         * A surface with no release has no phase, and the block is absent —
         * better than a bar announcing a window nobody set.
         */
        /*
         * The surface's NAME is the floor, not a bonus: the lockup is the
         * BRAND's mark, so a bar with only the lockup names the brand and
         * not the surface. Campaign form shows the name beside the program;
         * the release form keeps its phase; with neither, the name still
         * shows. The slot is empty only when there is nothing to say.
         */
        /*
         * CAMPAIGN FORM IS ONE LINE. The anchor writes it as a single accent
         * string — "Road to the Finals · Week 3" (`.hd__ss b`) — not a name
         * with a muted week stacked beneath it. The window is a qualifier on
         * the campaign's name, so it reads on the same line, joined by a
         * middot; with no week the name stands alone unchanged.
         */
        phaseLabel: drop.programName
            ? [drop.name, opts?.campaignWeekLabel].filter(Boolean).join(" · ")
            : status ? RELEASE_PHASE_LABEL[status] : drop.name,
        /*
         * The date qualifies the phase, so what it means depends on the phase:
         * a date ahead of an announced title is when it arrives; the same date
         * on a released one is when it did.
         */
        /*
         * Campaign form: the detail is the WEEK, and it arrives from the
         * caller only when a board exists — no leaderboard, no week, the
         * line suppresses (product ruling 2026-08-29). Release form keeps
         * its date qualifier.
         */
        /* Campaign form carries the week INLINE above, so there is no second line. */
        phaseDetail: drop.programName
            ? undefined
            : date && status
                ? `${RELEASE_DATE_PREFIX[status]} ${MONTH_DAY.format(new Date(date))}`
                : undefined,
    };
}

/**
 * The stage, in its identity state.
 *
 * The lockup is the title and type is the fallback — a publisher ships artwork
 * no font reproduces, so setting the name in Barlow beside their own logo reads
 * as a placeholder for the logo that should be there.
 *
 * `synopsis` before `description`: the synopsis is written for this position
 * (the anchor's is two sentences of story), while the description is the
 * directory blurb. Falling back to it keeps the band from being empty on a
 * surface that never filled the synopsis in.
 */
/**
 * The campaign window running right now, if one is.
 *
 * A brand campaign's stage is about the window, not a launch — "Road to the
 * Finals is live", with the time left in the round. Picks the window whose
 * span contains `now`; a campaign between rounds has none, which is a real
 * state and not a gap to fill with the next one.
 */
function activeWindow(drop: SpineDrop, now: number) {
    return (drop.campaignWindows ?? []).find(w =>
        typeof w.startsAt === "number" && typeof w.endsAt === "number" &&
        w.startsAt <= now && now < w.endsAt);
}

/** "4d 6h", "6h 12m", "48m" — the largest two units that are non-zero. */
function timeLeft(ms: number): string {
    const totalMinutes = Math.max(0, Math.floor(ms / 60000));
    const d = Math.floor(totalMinutes / 1440);
    const h = Math.floor(totalMinutes % 1440 / 60);
    const m = totalMinutes % 60;
    if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
    if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
    return `${m}m`;
}

/**
 * What the stage says when nothing is playing.
 *
 * ── IT NO LONGER ASSUMES A LAUNCH ──
 *
 * Every figure here used to be a game launch: `phaseLabel` was always
 * "Launching <date>", the stats were a countdown to release plus a roster
 * count, and the only action was a Pre-order link off `purchaseUrl`. That is
 * the publisher POV baked into the structure — and a BRAND campaign has none
 * of it, so Road to the Finals inherited a launch countdown it does not have
 * and a purchase button for a thing you cannot buy.
 *
 * Now each piece appears only when the Drop actually HAS it, and a campaign
 * window is a first-class alternative to a release. A surface with neither
 * says neither, which is a complete rendering rather than a degraded one.
 */
export function stageProps(
    drop: SpineDrop,
    now: number = Date.now(),
    /** The Drop's leaderboard reset. When known it IS the countdown — the board is the clock members play to. */
    boardPeriodEndsAt?: number,
): Omit<SurfaceStageProps, "state"> {
    const date = drop.release?.releaseDate;
    const days = date ? daysUntil(date, now) : undefined;
    const copy = povCopy(drop.pov, drop.dropType);
    const window = activeWindow(drop, now);

    /*
     * ONLY THE FIGURES THIS SURFACE ACTUALLY HAS.
     *
     * The anchor shows three — days to launch, community readiness, official
     * creators. Two of them are derivable from stored data. Readiness is a
     * campaign metric with no field behind it, so it is absent rather than
     * zeroed: a counter reading 0% claims the community has done nothing,
     * which is a statement, and a false one. The trio is a layout the stats
     * happen to make, not a shape that has to be filled.
     */
    const stats: SurfaceStageStat[] = [];
    /*
     * The running window leads, because it is the thing that repeats and the
     * thing a visitor can still act on. A launch countdown is the publisher
     * equivalent and only one of the two can be true at a time.
     */
    const countdownEndsAt = boardPeriodEndsAt !== undefined && boardPeriodEndsAt > now
        ? boardPeriodEndsAt
        : window?.endsAt;
    if (countdownEndsAt !== undefined) {
        stats.push({
            id: "window",
            label: copy.windowStatLabel,
            value: timeLeft(countdownEndsAt - now),
            detail: "Remaining",
        });
    } else if (days !== undefined) {
        stats.push({id: "launch", label: "To launch", value: String(days), detail: "Days"});
    }
    if (drop.memberCount) {
        stats.push({
            id: "members",
            label: drop.memberStatLabel ?? "Members",
            value: drop.memberCount.toLocaleString(),
            /*
             * The qualifier is authored, not assumed. "Joined" is a fact about
             * this surface; a programme open worldwide says so instead, and
             * that word is a claim we should not be making on its behalf.
             */
            detail: drop.memberStatDetail ?? "Joined",
        });
    }
    /*
     * NO FINALE DATE. The last window used to publish itself as a hero stat
     * with its date range, which nobody authored and which states something the
     * campaign has not: when the finale RUNS is not when its result is known.
     * Road to the Finals is filmed on those dates and its winners are not
     * announced until the November premiere, so printing the range told members
     * to expect an answer on a day there will not be one.
     *
     * The window itself stays — it drives the active-window logic above. If a
     * campaign ever wants its finale dated in the hero, that is an authored
     * stat, not one derived from the schedule.
     */
    return {
        imageUrl: pickHeroImage(drop),
        logoUrl: drop.logoImageUrl,
        name: drop.name,
        /*
         * The pulsing line above the lockup.
         *
         * A running campaign says it is running — the anchor's "Road to the
         * Finals is live". A title with a date says when it launches. A
         * surface with neither says nothing rather than inventing a phase.
         */
        /*
         * The CAMPAIGN is what's live — "Road to the Finals is live" — not the
         * window inside it; the window's name is the countdown stat's label.
         */
        phaseLabel: window
            ? `${drop.name} is live`
            : date ? `Launching ${MONTH_DAY.format(new Date(date))}` : undefined,
        synopsis: drop.synopsis ?? drop.tagline ?? drop.description,
        stats,
        /*
         * "Pre-order" ahead of release, `copy.purchaseCta` once it has shipped
         * — a released title still showing "Pre-order" is stale copy, not a
         * neutral default, so this is release-status-driven and not a constant.
         */
        actions: drop.purchaseUrl
            ? React.createElement(
                "a",
                {className: "sh-cta", href: drop.purchaseUrl, target: "_blank", rel: "noreferrer"},
                drop.release?.status === "RELEASED" ? copy.purchaseCta : copy.preLaunchCta,
            )
            : undefined,
    };
}

/*
 * This file used to declare its own `RosterMemberLike`/`RosterHighlightLike` —
 * hand-written mirrors of `DropRosterMember`/`RosterHighlight`, the pattern
 * the repo bans (a mirror keeps compiling while the schema moves; the drift
 * only surfaces at runtime). `rosterMember.tsx` already exists as the shared
 * home for roster derivations — split out of `CreatorTeamSection` for exactly
 * this reason — and already has the correct versions of two functions this
 * file had reinvented differently: `playerSource()` corrects a Twitch
 * numeric-id bug this file's caller was reintroducing by spreading
 * `m.playerSource` raw, and `highlightForMember()` matches by `creatorLabel`
 * only, where this file's own `highlightFor()` preferred `dropSlug` first —
 * a different rule that could pick a different clip than the legacy PAGE
 * path for the same person. Both are gone; this imports the real ones.
 */

/**
 * OFF-TITLE IS NOT THE SAME AS `offGame`.
 *
 * The endpoint computes `offGame` only for members who are LIVE — it answers
 * "is this person streaming something else RIGHT NOW". For an offline member it
 * is false while `gameName` still holds whatever they last played, so a card
 * built straight from those two fields printed another title's name on this
 * surface, unqualified and unbadged.
 *
 * `filterGameNames` is on the payload for exactly this: what the operator
 * declared counts as on-title. Matching is a containment test both ways because
 * the two names are not the same string — the roster says "Call of Duty: Modern
 * Warfare 4" and the filter says "Modern Warfare 4".
 */
/*
 * THE OFF-TITLE RULE LIVES IN ONE PLACE — re-exported, not re-implemented.
 *
 * This was a second copy of the containment match that `rosterCard.ts` also
 * carries for the shared resolver. Two copies of a rule drift, and this one
 * is load-bearing in three call sites here (the rail badge, HUB's featuring
 * choice, the featured card's kicker) as well as inside the resolver — so a
 * change made to one and not the other would show up as HUB and PAGE
 * disagreeing about the same member, which is the whole failure this project
 * exists to end.
 *
 * Kept as a named export from this module because its three consumers here
 * already import it by this name; only the implementation moved.
 */
export {isOffTitle};

/**
 * Does this roster's filter mode allow SAYING a member is off-title?
 *
 * `isOffTitle` above answers "is this person on another title" — a fact about
 * the member, true regardless of policy, and the right basis for deciding who
 * gets FEATURED (a surface should never feature someone repping another game,
 * whatever the mode). What the mode decides is separate: whether that fact is
 * allowed to appear on the page.
 *
 * This is the half `isOffTitle` never had. HUB read `filterGameNames` and never
 * `filterMode`, so it badged "Off title" under every policy including STRICT —
 * whose whole point is that off-title presence does not surface. PAGE has been
 * correct all along because the decision happens server-side for it, in
 * `applyRosterLiveFilter.ts`; HUB is being brought up to that same standard
 * here rather than growing filter logic of its own.
 *
 * ── STRICT does not REMOVE the member ──
 *
 * It suppresses the CLAIM, not the person. `LiveFilterMode.STRICT` is defined
 * on the model as "off-game members render offline and are excluded from live
 * counts", and the endpoint implements exactly that: it withholds a member who
 * is off-game *and live* (`get_drops_slug_roster.ts`, `hiddenByFilter`), while
 * offline members always render — "a roster is the team, not just whoever
 * happens to be streaming".
 *
 * So by the time a member reaches this file under STRICT, any live off-game one
 * is already gone and what is left is an OFFLINE member whose last observed
 * game was another title. Dropping them from the rail would shrink a
 * publisher's roster on the basis of what someone happened to play yesterday.
 * The badge and the last-played line are what get suppressed.
 *
 * ── An unrecognised mode never hides ──
 *
 * `filterMode` arrives as a bare `string` on `DropRosterResponse`, so this
 * tests for STRICT explicitly instead of "not OFF and not BADGE". The backend
 * can afford the inverse because its `mode` is a closed union; here a typo or a
 * mode added later would silently strip every roster's badges. Defaults never
 * hide — the same invariant `applyRosterLiveFilter` states for itself.
 */
export function suppressesOffTitle(filterMode: string | undefined): boolean {
    return String(filterMode ?? "").toUpperCase() === "STRICT";
}

/**
 * The roster payload, as the rail's members.
 *
 * A THIN ADAPTER over `resolveRosterCard` — it maps the shared resolved card
 * onto the rail's own prop shape and decides nothing itself. Every question it
 * used to answer inline (which image wins, what the line under the name says,
 * whether "unknown" is "offline") is now answered once, in one place, for both
 * this rail and PAGE's card.
 *
 * `filterMode` is the roster's own `LiveFilterMode`, straight off the response.
 * It is a parameter rather than something derived here because this file
 * decides nothing about policy — it renders what the roster's operator set.
 * Omitting it keeps the previous behaviour (badge), which is what OFF and
 * BADGE both want anyway.
 */
export function rosterMembers(
    members: readonly RosterMember[],
    filterGameNames: readonly string[],
    filterMode?: string,
    /* The roster's authored schema, off the payload. Empty is the common case. */
    rosterFields: readonly RosterFieldSpec[] = [],
): SurfaceRosterMember[] {
    const suppress = suppressesOffTitle(filterMode);
    return members.map(m => {
        /*
         * `isOffTitle` stays the HUB-side input to the shared resolver.
         *
         * The resolver reads `offGame`, which the endpoint computes only for
         * members who are LIVE. HUB additionally treats an OFFLINE member whose
         * last observed game was another title as off-title — that is what
         * `filterGameNames` is on the payload for, and it is why a card built
         * straight from `offGame` printed another title's name unqualified.
         * Passing it in as `offGame` lets one resolver serve both without HUB
         * growing its own copy of the live-state rules.
         */
        /*
         * `filterGameNames` goes to the resolver rather than being pre-applied
         * here. HUB used to compute off-title itself and hand the answer in as
         * a forged `offGame`, which worked but left PAGE — which had no such
         * override — printing a rival title's name on every offline member.
         * One rule, one place, both hosts.
         */
        const card = resolveRosterCard(m, {
            suppressOffTitle: suppress,
            filterGameNames,
            fieldSpecs: rosterFields,
            fieldValues: m.fieldValues,
        });
        const offTitle = isOffTitle(m, filterGameNames);

        return {
            id: card.key,
            name: card.name,
            /*
             * "UNKNOWN" IS NOT "OFFLINE", and the COPY for it belongs here.
             *
             * `isLive` alone cannot tell a confirmed-offline member from one the
             * detection job has not observed recently — both read `false`. A
             * roster whose whole detector has stalled then shows every member
             * with a firm "Last live · X", claiming knowledge the surface does
             * not have. The resolver carries that distinction as a STATE and
             * deliberately supplies no words for it, because the right words
             * differ per host; this rail says so plainly.
             */
            detail: card.liveState === RosterLiveState.UNKNOWN
                ? "Live status unavailable"
                /*
                 * The resolver withholds an off-title member's last-played line
                 * and hands the name over in `offTitleGame` for a host that
                 * shows it deliberately, badge alongside — this rail, under a
                 * mode that badges.
                 */
                : !m.isLive && offTitle && !suppress && card.offTitleGame
                    ? `Last live · ${card.offTitleGame}`
                    : card.detail,
            imageUrl: card.imageUrl,
            live: m.isLive,
            offTitle: card.liveState === RosterLiveState.LIVE_OFF_TITLE || offTitle && !suppress,
            /* Resolved by the shared resolver above — the rail draws them per type. */
            fields: card.fields,
        };
    });
}

/** Steam/publisher news, as the rail's items. */
export function newsItems(items: readonly { id?: string; title?: string; url?: string; imageUrl?: string; author?: string }[]): SurfaceNewsItem[] {
    return items.map(n => ({
        id: n.id ?? n.url ?? "",
        title: n.title ?? "",
        author: n.author,
        imageUrl: n.imageUrl,
        url: n.url,
    }));
}

/** `modes[]`, as the strip's cards. */
export function modeCards(modes: readonly { kicker?: string; title?: string; meta?: string; upcoming?: boolean }[]): SurfaceStripCard[] {
    return modes.map((m, i) => ({
        id: `mode-${i}`,
        kicker: m.kicker,
        title: m.title ?? "",
        meta: m.meta,
        /*
         * `upcoming` is the only state the data carries, so it is the only
         * badge shown. "Next" would claim an ordering nothing has decided.
         */
        badge: m.upcoming ? {label: "Soon", kind: "soon" as const} : undefined,
    }));
}
